import React, { useRef } from 'react';
import classnames from 'classnames';
import './FileUpload.css';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import './../input.scss';
import { t } from '../../../locales/translate';
import { imageUrl, FileRepresenting } from '../../../model/asset';
import { FormField } from '../../../model/form';
import { readBytesUrl } from '../../../util/util';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

interface Props extends FormField<FileRepresenting | null> {
  alt?: string;
  label?: string;
  error?: boolean;
  description?: string;
  icon?: IconName;
  multiple?: boolean;
  required?: boolean;
  accept?: string;
}

export function FormImageUpload({
  inputId,
  value,
  alt,
  label,
  error,
  multiple,
  className,
  description,
  style,
  icon,
  required,
  accept = 'image/*',
  onChange,
}: Props): JSX.Element {
  const fileUpload: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

  async function handleOnChange(fileList: FileList): Promise<void> {
    const files = Array.from(fileList);
    if (files.length <= 0) {
      onChange?.(null);
    }
    const encodedImage = await readBytesUrl(files[0], true);
    onChange?.({ type: 'base64URL', url: encodedImage });
  }

  return (
    <div className={classnames('file-upload', 'input', { 'input--with-icon': icon }, className)} style={style}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div className="input__container input__container--full">
        <div className="input__input-wrapper">
          {label && (
            <label className="input__label" htmlFor={inputId}>
              {label}
              {!required && ` (${t.general.optional})`}
            </label>
          )}
          {value && (
            <div className="file-upload__wrapper">
              <img className="file-upload__image" src={imageUrl(value)} alt={alt} />
              <div
                className="file-upload__remove-button"
                onClick={() => {
                  onChange?.(null);
                }}
              >
                <Icon name="remove-circle--sm" />
              </div>
            </div>
          )}
          {!value && (
            <div className="file-upload__input">
              <input
                id={inputId}
                type="file"
                ref={fileUpload}
                className="input__input"
                accept={accept}
                onChange={(e) => {
                  if (e.target.files) {
                    handleOnChange(e.target.files);
                  }
                }}
                required={required}
                multiple={multiple}
              />
            </div>
          )}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={error ? true : false}>
            <div className="input__error-message">{error}</div>
          </Collapse>
        </div>
      </div>
    </div>
  );
}
